import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, Paper,
  TextField, Typography,
} from '@mui/material';
import { InvoiceBrandingService } from '../../services/invoiceBranding.service';
import { AuthService } from '../../services/auth.service';
import type { InvoiceBranding } from '../../interfaces/sales/invoiceBranding.interface';
import { useToast } from '../../context/ToastContext';

const EMPTY_FORM: InvoiceBranding = {
  institutionId: '',
  displayName: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  tin: '',
  vatRegNo: '',
  areaCode: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankName: '',
  bankBranch: '',
  bankRoutingNo: '',
  bankSwift: '',
  paymentInstructions: '',
  footerNote: '',
  signatureLeftLabel: '',
  signatureRightLabel: '',
  showSignatures: false,
  showAmountInWords: true,
  hasLogo: false,
};

const MAX_LOGO_BYTES = 1_048_576; // 1 MB, mirrors the server-side limit

const InvoiceBrandingSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<InvoiceBranding>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    load();
    return () => { if (logoUrlRef.current) URL.revokeObjectURL(logoUrlRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setLoading(true);
      const branding = await InvoiceBrandingService.getByInstitution(institutionId);
      applyBranding(institutionId, branding);
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoice branding');
    } finally {
      setLoading(false);
    }
  };

  const updateLogoUrl = (next: string | null) => {
    setLogoUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return next;
    });
    logoUrlRef.current = next;
  };

  const applyBranding = async (institutionId: string, branding: InvoiceBranding) => {
    setForm({ ...EMPTY_FORM, ...branding, institutionId });
    if (branding.hasLogo) {
      try {
        const blob = await InvoiceBrandingService.getLogoBlob(institutionId);
        updateLogoUrl(URL.createObjectURL(blob));
      } catch {
        updateLogoUrl(null);
      }
    } else {
      updateLogoUrl(null);
    }
  };

  const handleText = (k: keyof InvoiceBranding) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const handleCheck = (k: 'showSignatures' | 'showAmountInWords') =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.checked });

  const handleSave = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setSaving(true);
      const saved = await InvoiceBrandingService.upsert({ ...form, institutionId });
      setForm({ ...EMPTY_FORM, ...saved, institutionId });
      toast.showSuccess('Invoice branding saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save invoice branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      toast.showApiError(new Error('too large'), 'Logo must be 1 MB or smaller');
      return;
    }
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const saved = await InvoiceBrandingService.uploadLogo(institutionId, file);
      await applyBranding(institutionId, saved);
      toast.showSuccess('Logo uploaded');
    } catch (err) {
      toast.showApiError(err, 'Failed to upload logo');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      const saved = await InvoiceBrandingService.deleteLogo(institutionId);
      await applyBranding(institutionId, saved);
      toast.showSuccess('Logo removed');
    } catch (err) {
      toast.showApiError(err, 'Failed to remove logo');
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const text = (label: string, k: keyof InvoiceBranding, extra: object = {}) => (
    <TextField
      label={label}
      value={(form[k] as string) ?? ''}
      onChange={handleText(k)}
      fullWidth
      {...extra}
    />
  );

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Invoice Branding</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        <Typography variant="subtitle1">Logo</Typography>
        {logoUrl && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img src={logoUrl} alt="Invoice logo" style={{ maxHeight: 64 }} />
            <Button color="error" onClick={handleLogoDelete}>Remove Logo</Button>
          </Box>
        )}
        <Button variant="outlined" component="label">
          {logoUrl ? 'Replace Logo' : 'Upload Logo'}
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept="image/png,image/jpeg"
            aria-label="Logo file"
            onChange={handleLogoSelected}
          />
        </Button>
        <Typography variant="caption" color="text.secondary">
          PNG or JPEG, max 1 MB. Rendered at up to 48pt height on the PDF.
        </Typography>

        <Divider />
        <Typography variant="subtitle1">Header</Typography>
        {text('Display Name', 'displayName')}
        {text('Address', 'address')}
        {text('Phone', 'phone')}
        {text('Email', 'email')}
        {text('Website', 'website')}

        <Divider />
        <Typography variant="subtitle1">Registration</Typography>
        {text('TIN', 'tin')}
        {text('VAT Reg No', 'vatRegNo')}
        {text('Area Code', 'areaCode')}

        <Divider />
        <Typography variant="subtitle1">Bank &amp; Payment</Typography>
        {text('Bank Account Name', 'bankAccountName')}
        {text('Bank Account Number', 'bankAccountNumber')}
        {text('Bank Name', 'bankName')}
        {text('Bank Branch', 'bankBranch')}
        {text('Routing No', 'bankRoutingNo')}
        {text('SWIFT', 'bankSwift')}
        {text('Payment Instructions', 'paymentInstructions', { multiline: true, minRows: 2 })}

        <Divider />
        <Typography variant="subtitle1">Print Options</Typography>
        <FormControlLabel
          control={<Checkbox checked={form.showAmountInWords} onChange={handleCheck('showAmountInWords')} />}
          label="Show amount in words"
        />
        <FormControlLabel
          control={<Checkbox checked={form.showSignatures} onChange={handleCheck('showSignatures')} />}
          label="Show signature lines"
        />
        {text('Left Signature Label', 'signatureLeftLabel', { placeholder: 'Prepared By' })}
        {text('Right Signature Label', 'signatureRightLabel', { placeholder: 'Authorized By' })}
        {text('Footer Note', 'footerNote', { multiline: true, minRows: 2 })}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>Save</Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default InvoiceBrandingSettings;
